/**
 * Tradução de erro do Firebase para frase que ajuda quem está na tela.
 *
 * Função pura, sem React e sem SDK: é a camada que decide o que a pessoa lê
 * quando algo falha, e isso merece teste próprio.
 *
 * A regra que organiza tudo aqui: só vira mensagem própria o erro cuja causa a
 * gente conhece e cuja saída a gente sabe apontar. Erro desconhecido chega
 * inteiro — mascarar falha que ninguém previu é trocar um texto feio por um
 * silêncio, e silêncio não se depura.
 */

export interface FalhaFirebase {
  code?: unknown;
  message?: unknown;
}

function codigoDe(e: unknown): string | null {
  if (typeof e !== "object" || e === null) return null;
  const codigo = (e as FalhaFirebase).code;
  return typeof codigo === "string" ? codigo : null;
}

function textoDe(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === "object" && e !== null) {
    const m = (e as FalhaFirebase).message;
    if (typeof m === "string" && m !== "") return m;
  }
  return "Não foi possível concluir a ação.";
}

/** A pessoa desistiu — fechou a janela, clicou duas vezes. Não é erro para mostrar. */
const DESISTENCIA = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

export function ehDesistencia(e: unknown): boolean {
  const codigo = codigoDe(e);
  return codigo !== null && DESISTENCIA.has(codigo);
}

/**
 * Erros de autenticação cuja causa é de configuração do projeto, não da pessoa.
 *
 * Enquanto o provedor Google não estiver habilitado, o SDK devolve
 * `auth/configuration-not-found` e o texto cru é "Firebase: Error
 * (auth/configuration-not-found)." — que não diz a ninguém o que fazer. A
 * mensagem aqui nomeia a causa e diz de quem é a próxima ação. No dia em que o
 * provedor for habilitado, este código deixa de acontecer e nada fica mascarado.
 */
const AUTH: Record<string, string> = {
  "auth/configuration-not-found":
    "O login com Google ainda não está habilitado neste projeto. " +
    "Um administrador precisa ativá-lo no Console do Firebase, em Authentication › Sign-in method.",
  "auth/operation-not-allowed":
    "O login com Google está desativado neste projeto. " +
    "Um administrador precisa habilitá-lo no Console do Firebase.",
  "auth/unauthorized-domain":
    "Este endereço ainda não está autorizado a fazer login. " +
    "Um administrador precisa incluí-lo nos domínios autorizados do Firebase Authentication.",
  "auth/invalid-api-key":
    "A chave de configuração deste ambiente é inválida. Confira o arquivo .env do Parker.",
  "auth/api-key-not-valid":
    "A chave de configuração deste ambiente é inválida. Confira o arquivo .env do Parker.",

  "auth/popup-blocked":
    "O navegador bloqueou a janela do Google. Libere os pop-ups para este endereço e tente de novo.",
  "auth/network-request-failed":
    "Não foi possível falar com o Google. Verifique sua conexão e tente de novo.",
  "auth/too-many-requests": "Muitas tentativas seguidas. Espere um instante e tente de novo.",
  "auth/user-disabled": "Esta conta está desativada. Procure um administrador do grupo.",
  "auth/account-exists-with-different-credential":
    "Já existe uma conta com este e-mail criada por outro método de login.",
  "auth/internal-error": "O serviço de login falhou. Tente de novo em instantes.",
};

/** Erros das callables. O servidor já escreve em português; aqui fica a infraestrutura. */
const FUNCTIONS: Record<string, string> = {
  "functions/unauthenticated": "Sua sessão expirou. Entre novamente.",
  "functions/unavailable": "O serviço está indisponível no momento. Tente de novo.",
  "functions/deadline-exceeded": "A operação demorou demais. Tente de novo.",
  "functions/cancelled": "A operação foi cancelada.",
};

/**
 * Indica se a falha é de configuração do projeto — algo que nenhuma tentativa
 * da pessoa resolve. A tela de login usa isso para trocar o tom: em vez de
 * "tente de novo", explica o que falta e para quem pedir.
 */
export function ehFalhaDeConfiguracao(e: unknown): boolean {
  const codigo = codigoDe(e);
  return (
    codigo === "auth/configuration-not-found" ||
    codigo === "auth/operation-not-allowed" ||
    codigo === "auth/unauthorized-domain" ||
    codigo === "auth/invalid-api-key" ||
    codigo === "auth/api-key-not-valid"
  );
}

export function mensagemDeErro(e: unknown): string {
  const codigo = codigoDe(e);
  if (codigo === null) return textoDe(e);

  const conhecido = AUTH[codigo] ?? FUNCTIONS[codigo];
  if (conhecido) return conhecido;

  // As callables do Parker escrevem as próprias mensagens; estas passam inteiras.
  if (codigo.startsWith("functions/")) return textoDe(e);

  // Erro de auth que não mapeamos: mostra o texto do SDK em vez de inventar
  // uma explicação. Pelo menos o código fica visível para quem for investigar.
  return textoDe(e);
}
